import { useLayoutEffect, useState } from "react";
import Layout from "../../components/Layout";
import { Heading } from "@/components/typography/heading";
import { Badge } from "@/components/ui/badge";
import {
    CHROMA_TARGETS,
    LIGHTNESS_TARGETS,
    RAMPS,
    STEPS,
} from "./palette-strategies";

function token(name: string, step: string) {
    return `var(--${name}-${step})`;
}

function tokenVar(name: string, step: string) {
    return `--${name}-${step}`;
}

/** Resolve any CSS color (including oklch) to #RRGGBB via canvas sRGB. */
function cssColorToHex(cssColor: string): string | null {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d", { colorSpace: "srgb" });
    if (!ctx) return null;
    ctx.fillStyle = cssColor;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `#${[r, g, b]
        .map((c) => c.toString(16).padStart(2, "0").toUpperCase())
        .join("")}`;
}

function ColorStop({
    name,
    step,
    empty = false,
}: {
    name: string;
    step: string;
    empty?: boolean;
}) {
    const [oklch, setOklch] = useState<string>("");
    const [hex, setHex] = useState<string>("");

    useLayoutEffect(() => {
        if (empty) return;
        const raw = getComputedStyle(document.documentElement)
            .getPropertyValue(tokenVar(name, step))
            .trim();
        setOklch(raw);
        // Prefer the authored token; computed backgroundColor is often oklch too.
        setHex(cssColorToHex(raw || token(name, step)) ?? "");
    }, [name, step, empty]);

    if (empty) {
        return (
            <div>
                <div className="mb-1 h-14 rounded-md border border-dashed border-border" />
                <div className="text-tiny/4 font-medium text-muted-foreground">{step}</div>
            </div>
        );
    }

    return (
        <div>
            <div
                className="mb-1 h-14 rounded-md border border-black/5"
                style={{ background: token(name, step) }}
            />
            <div className="text-tiny/4 font-medium">{step}</div>
            {oklch ? (
                <div
                    className="mt-0.5 break-all font-mono text-[8px]/4 text-muted-foreground"
                    title={oklch}
                >
                    {oklch}
                </div>
            ) : null}
            {hex ? (
                <div
                    className="mt-0.5 font-mono text-tiny/4 text-muted-foreground"
                    title={hex}
                >
                    {hex}
                </div>
            ) : null}
        </div>
    );
}

/** Gray tokens currently defined in index.css (no 25 / 50 / 950 yet). */
const GRAY_STEPS = new Set([
    "100", "200", "300", "400", "500", "600", "700", "800", "900",
]);

function RampRow({
    name,
    definedSteps,
}: {
    name: string;
    definedSteps?: Set<string>;
}) {
    return (
        <div className="grid grid-cols-6 gap-2 lg:grid-cols-12">
            {STEPS.map((step) => (
                <ColorStop
                    key={step}
                    name={name}
                    step={step}
                    empty={definedSteps ? !definedSteps.has(step) : false}
                />
            ))}
        </div>
    );
}

export default function Palette() {
    return (
        <Layout>
            <div className="space-y-12 p-4">
                <div>
                    <Heading type="h1" className="mb-2">Color system</Heading>
                    <p className="max-w-3xl text-muted-foreground">
                        Live tokens from <code className="text-sm">src/index.css</code>.
                        Mix chrome: header{" "}
                        <code className="text-sm">cyan-200</code>, CTA{" "}
                        <code className="text-sm">cyan-300</code>,{" "}
                        <code className="text-sm">--primary</code> → cyan-500
                        (controls + links). Hand-tuned OKLCH ramps for feedback
                        and soft UI. Feedback hues: success 156, warning 86,
                        destructive 20.
                    </p>
                </div>

                <section className="space-y-4">
                    <Heading type="h2">How the hand-tuned ramps work</Heading>
                    <ul className="max-w-3xl list-disc space-y-2 pl-5 text-muted-foreground">
                        <li>
                            <span className="text-foreground font-medium">Hue</span> — fixed per
                            family (no Tailwind-style drift).
                        </li>
                        <li>
                            <span className="text-foreground font-medium">Lightness</span> — one
                            shared ladder. Steps 500 / 600 / 700 sit near 3:1 / 4.5:1 / 7:1 for
                            green on white so body text on white is safe at 600+ in every family.
                        </li>
                        <li>
                            <span className="text-foreground font-medium">Chroma</span> — one
                            shared <em>target</em> list (table below). Actual C is{" "}
                            <code className="text-sm">min(target, max-in-gamut)</code> at that L
                            and hue — yellow/cyan clamp more than purple/red.
                        </li>
                        <li>
                            Targets were an eyeballed hump, not imported from Material/Huetone.
                            Compared to fixed-C, % of max-C, and HCT; hand-tuned won on perceived
                            evenness.
                        </li>
                    </ul>

                    <div className="overflow-x-auto">
                        <table className="w-full max-w-3xl text-left text-sm">
                            <thead>
                                <tr className="border-b border-border text-muted-foreground">
                                    <th className="py-2 pr-4 font-medium">Step</th>
                                    <th className="py-2 pr-4 font-medium">L %</th>
                                    <th className="py-2 font-medium">C target</th>
                                </tr>
                            </thead>
                            <tbody>
                                {STEPS.map((step, i) => (
                                    <tr key={step} className="border-b border-border/60">
                                        <td className="py-1.5 pr-4 tabular-nums">{step}</td>
                                        <td className="py-1.5 pr-4 tabular-nums">
                                            {LIGHTNESS_TARGETS[i].L}
                                        </td>
                                        <td className="py-1.5 tabular-nums">
                                            {CHROMA_TARGETS[i].C}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="space-y-4">
                    <Heading type="h2">Recipes</Heading>
                    <p className="max-w-3xl text-muted-foreground">
                        Loudness is chosen per component by picking rungs — not one tint % for
                        everything. Alert washes use{" "}
                        <code className="text-sm">*-25</code>, quieter than soft-badge{" "}
                        <code className="text-sm">*-100</code>.
                    </p>
                    <div className="space-y-3">
                        <code className="text-sm text-muted-foreground">
                            soft badge — Badge variants (default = cyan soft)
                        </code>
                        <div className="space-x-2">
                            {RAMPS.map(({ name }) => (
                                <Badge
                                    key={name}
                                    variant={name === "cyan" ? "default" : name}
                                    className="capitalize"
                                >
                                    {name}
                                </Badge>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <code className="text-sm text-muted-foreground">
                            loud badge — bg-*-600 text-white
                        </code>
                        <div className="space-x-2">
                            {RAMPS.map(({ name }) => (
                                <Badge
                                    key={name}
                                    className="capitalize text-white"
                                    style={{ background: token(name, "600") }}
                                >
                                    {name}
                                </Badge>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="space-y-8">
                    <Heading type="h2">Generated ramps</Heading>
                    {RAMPS.map(({ name, hue, role }) => (
                        <div key={name}>
                            <Heading type="h5" tag="h3">{name}</Heading>
                            <p className="mb-3 text-sm text-muted-foreground">
                                hue {hue} — {role}
                            </p>
                            <RampRow name={name} />
                        </div>
                    ))}
                    <div>
                        <Heading type="h5" tag="h3">gray</Heading>
                        <p className="mb-3 text-sm text-muted-foreground">
                            hue 0 — Neutrals (100–900 defined; 25 / 50 / 950 empty for now).
                            White and black are separate tokens.
                        </p>
                        <RampRow name="gray" definedSteps={GRAY_STEPS} />
                    </div>
                </section>
            </div>
        </Layout>
    );
}
